/**
 * SettingsLayout (TRA-1170): the /settings layout route. SettingsFrame
 * draws a sub-nav built from SETTINGS_SECTIONS on the left, grouped by
 * heading, and an <Outlet /> for the section on the right. The second
 * layout route in the app after AppLayout, and the same mechanism:
 * NavLink carries the active state.
 *
 * SectionRoute is the element every section route renders through. It
 * enforces the registry's `access` and `navRoute` the way OwnerRoute did
 * for /billing and NavRoute does for the demo suite. Ownership comes from
 * /me; the nav config from the team whose data the app shows, which under
 * impersonation is the impersonated team (TeamContext.navConfigLoaded). A
 * gated section stays blank while the config that decides it is pending
 * (a refresh must not bounce the owner off their own page), then renders
 * or goes back to /settings. A failed load settles on the fallback the
 * sidebar also runs in, so a listed section never stays blank for good.
 */

import { Navigate, Outlet } from 'react-router-dom'

import { useTeam } from '@/contexts/TeamContext'

import {
  canAccessSection,
  GROUP_LABELS,
  SETTINGS_GROUPS,
  SETTINGS_ROOT,
  SETTINGS_SECTIONS,
  settingsPath,
  type SettingsAccessContext,
  type SettingsSection,
} from './sections'
import { SettingsFrame, type SettingsNavGroup } from './SettingsFrame'

function useSettingsAccess(): SettingsAccessContext {
  const {
    activeTeam,
    isEmployee,
    impersonatingTeamId,
    hiddenNavItems,
    navConfigLoaded,
    navConfigSettled,
  } = useTeam()
  return {
    isOwner: activeTeam?.is_owner ?? false,
    permissions: activeTeam?.permissions ?? [],
    isEmployee,
    impersonating: impersonatingTeamId !== null,
    hiddenNavItems,
    navConfigLoaded,
    navConfigSettled,
  }
}

export function SettingsLayout(): React.JSX.Element {
  const access = useSettingsAccess()
  const visible = SETTINGS_SECTIONS.filter((section) => canAccessSection(section, access))
  const groups: SettingsNavGroup[] = SETTINGS_GROUPS.map((group) => ({
    label: GROUP_LABELS[group],
    entries: visible
      .filter((section) => section.group === group)
      .map((section) => ({
        to: settingsPath(section.path),
        label: section.label,
        icon: section.icon,
      })),
  }))
  return (
    <SettingsFrame navLabel="Settings" title="Settings" groups={groups}>
      <Outlet />
    </SettingsFrame>
  )
}

export function SectionRoute({ section }: { section: SettingsSection }): React.JSX.Element | null {
  const access = useSettingsAccess()
  if (!canAccessSection(section, access)) {
    // Blank only while the answer is still in flight. Off impersonation
    // navConfigLoaded is meLoaded, which also carries ownership; under it
    // the owner check always passes, so the nav config is the only wait.
    return access.navConfigLoaded ? <Navigate to={SETTINGS_ROOT} replace /> : null
  }
  // A listed nav-gated section still waits out a pending config: the
  // fallback lists Review's sections for every team, and one that hides
  // Review must not get them on screen before the real answer arrives. A
  // failed load settles on that fallback, the same degraded mode the
  // sidebar runs in, rather than blanking the section for good.
  if (section.navRoute !== undefined && !access.navConfigSettled) return null
  const Component = section.Component
  return <Component section={section} />
}
